import type { ComponentType, ReactNode } from 'react';
import { useMemo } from 'react';

import { useControlledState } from '@/js/libs/hooks/useControlledState';
import { DropdownList, Button, Icons } from '@/js/libs/ui/src/V2';

import type { ColumnBasePropsV2 } from 'src/types.ts';

type SwitchableColumnBase<TName extends string> = Partial<Omit<ColumnBasePropsV2<TName>, 'columnSwitch'>>;

interface SwitchableColumn<TName extends string, TColumnProps> extends SwitchableColumnBase<TName> {
  name: TName;
  title: string;
  component: ComponentType<TColumnProps>;
}

export type SwitchableColumns<TName extends string, TColumnProps> = [
  SwitchableColumn<TName, TColumnProps>,
  ...SwitchableColumn<TName, TColumnProps>[],
];

interface UseColumnSwitchProps<TName extends string, TColumnProps> {
  columns: SwitchableColumns<TName, TColumnProps>;
  defaultColumn: TName;
  dataTestId?: string;
  selectedColumn?: TName;
  onColumnChange?: (column: TName) => void;
}

export const useColumnSwitch = <TName extends string, TColumnProps>({
  columns,
  defaultColumn,
  dataTestId,
  selectedColumn,
  onColumnChange,
}: UseColumnSwitchProps<TName, TColumnProps>): SwitchableColumn<TName, TColumnProps> & { columnSwitch: ReactNode } => {
  const [selected, setSelected] = useControlledState(selectedColumn, defaultColumn, onColumnChange);
  const active = useMemo(() => columns.find((column) => column.name === selected) ?? columns[0], [columns, selected]);

  const columnSwitch = useMemo(
    () => (
      <DropdownList
        renderButton={(isShown) => (
          <Button ghost stopPropagation dataTestId={dataTestId ? `${dataTestId}-switch` : undefined}>
            <Icons.Chevron align={isShown ? 'top' : 'bottom'} />
          </Button>
        )}>
        {columns.map(({ name, title }) => (
          <DropdownList.DropdownItem key={name} isSelected={name === selected} closeOnClick={false} onClick={() => setSelected(name)}>
            {title}
          </DropdownList.DropdownItem>
        ))}
      </DropdownList>
    ),
    [columns, selected, dataTestId, setSelected],
  );

  return useMemo(
    () => ({
      ...active,
      columnSwitch,
      dataTestId: active.dataTestId ?? dataTestId,
    }),
    [active, columnSwitch, dataTestId],
  );
};
